import { useState } from "react";
import { LocaleLink } from "../i18n/LocaleLink";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { m } from "motion/react";
import { entrance } from "../ds/core/motion";
import {
  Users, Package, Route, Navigation, HardHat, Receipt,
  ArrowRight, MapPin, ShieldCheck, FolderKanban, Quote,
  BarChart3, Eye, Clock, CalendarClock, FileText, Image as ImageIcon, Car,
} from "lucide-react";
import { Container, Eyebrow } from "../components/ui";
import FeatureOverview from "../components/FeatureOverview";
import {
  MotionProvider, Reveal, BentoCard, Sparkline, Marquee,
  ConnectedSteps, type ConnectedStep, Button as DSButton,
  EASE, IconGridCta, type CtaGridIcon, PageHeading, SectionHeading,
} from "../ds/core";
import { PricingPlanCard, PricingToggle, useIsDesktop } from "../components/PricingTable";
import { PROJECT_PLANS } from "../content/pricing";
import { TRIAL_PATH as TRIAL } from "../config/entrypoints";

const HERO_BG = "https://framerusercontent.com/images/gvrZUoowU6t5rwpwtONkkVWoFSU.png";

/* Faint white tile grid used on the photo / coloured panels (fades downward). */
const GRID_W = {
  backgroundImage:
    "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
  backgroundSize: "64px 64px",
  maskImage: "linear-gradient(to bottom, black, transparent)",
  WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
} as const;

/* ─────────────────────────────────────────────────────────────────────────
   1. HERO — full-height photo panel + live job-cost interface
   ───────────────────────────────────────────────────────────────────────── */

const LINE_ITEMS = [
  { labelKey: "panel.line.crew", value: "6", icon: HardHat },
  { labelKey: "panel.line.subs", value: "2", icon: Users },
  { labelKey: "panel.line.extra", value: "4", icon: Receipt },
  { labelKey: "panel.line.docs", value: "12", icon: FolderKanban },
  { labelKey: "panel.line.trips", value: "18", icon: Navigation },
];

const DOC_ROWS = [
  { key: "row.photos", icon: Eye, meta: "12" },
  { key: "row.files", icon: FolderKanban, meta: "4" },
];
const TRACK_ROWS = [
  { key: "row.trip", icon: MapPin },
  { key: "row.fleet", icon: Navigation },
];
const REPORT_ROWS = [
  { key: "row.timesheets", icon: BarChart3 },
  { key: "row.activity", icon: Clock },
];

function JobCostPanel() {
  const { t } = useLocale();
  // No backdrop-blur on this panel: 95% white already hides the backdrop, and the
  // live panel inside animates forever, so Chrome re-blurred every frame for
  // nothing (DIT-553).
  return (
    <div className="w-full rounded-2xl border border-white/60 bg-white/95 p-4 shadow-[0_36px_80px_-32px_rgba(2,6,23,0.7)] sm:p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-ds-14 font-semibold text-ds-ink">{t("projectManagement:jobcost.title")}</p>
          <p className="text-[12px] text-ds-ink-soft">{t("projectManagement:jobcost.meta")}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ds-pos/15 px-2.5 py-1 text-[12px] font-semibold text-ds-pos">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ds-pos opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-ds-pos" />
          </span>
          {t("projectManagement:jobcost.live")}
        </span>
      </div>

      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <span className="text-[13px] text-ds-ink-soft">{t("projectManagement:panel.hoursLabel")}</span>
          <span className="text-[13px] font-semibold text-ds-ink">184 / 240 h</span>
        </div>
        <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-bg-subtle">
          <m.div className="h-full rounded-full bg-ds-accent" initial={entrance({ width: 0 })} animate={{ width: "77%" }} transition={{ duration: 1.1, ease: EASE, delay: 0.1 }} />
        </div>
        <p className="mt-1.5 text-[12px] text-ds-ink-soft">{t("projectManagement:panel.hoursUsage")}</p>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        {LINE_ITEMS.map((it, i) => (
          <m.div key={it.labelKey} initial={entrance({ opacity: 0, y: 6 })} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 + i * 0.12, duration: 0.4, ease: EASE }}
            className="flex items-center gap-3 rounded-xl bg-bg-subtle px-3 py-2">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white text-ds-accent">
              <it.icon size={14} strokeWidth={2} />
            </span>
            <span className="flex-1 text-[13px] text-ds-ink-soft">{t(`projectManagement:${it.labelKey}`)}</span>
            <span className="text-[13px] font-semibold text-ds-ink">{it.value}</span>
          </m.div>
        ))}
      </div>

      <m.div initial={entrance({ opacity: 0, y: 8 })} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.45, ease: EASE }}
        className="mt-4 flex items-center justify-between rounded-xl bg-ds-ink px-4 py-3">
        <span className="text-[13px] font-medium text-white/80">{t("projectManagement:panel.statusLabel")}</span>
        <span className="inline-flex items-center gap-2 text-white">
          <span className="text-ds-18 font-bold">{t("projectManagement:panel.status")}</span>
          <span className="rounded-full bg-ds-pos/30 px-2 py-0.5 text-[12px] font-semibold text-white">12d</span>
        </span>
      </m.div>
    </div>
  );
}

function CostControlHero() {
  const { market, t } = useLocale();
  return (
    <PageHeading
      eyebrow={t("projectManagement:hero.eyebrow")}
      title={<>{t("projectManagement:hero.title.pre")} <span className="text-white/55">{t("projectManagement:hero.title.highlight")}</span></>}
      intro={market === "belgium"
        ? t("projectManagement:hero.intro.be")
        : t("projectManagement:hero.intro.intl")}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   2. WHY TEAMS ADD PROJECT — dark hero-structured panel
   ───────────────────────────────────────────────────────────────────────── */

function WhyProject() {
  const { t } = useLocale();
  return (
    <MotionProvider>
      <section className="bg-bg font-ds">
        <div className="px-4 py-3">
          <div className="relative overflow-hidden rounded-[32px] bg-ds-hero">
            <div className="pointer-events-none absolute inset-0" style={GRID_W} />
            <Container className="relative py-16 md:py-24">
              <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                <div>
                  <SectionHeading tone="dark" size="lg" eyebrow={t("projectManagement:why.eyebrow")} title={t("projectManagement:why.title")} />
                  <p className="mt-5 max-w-[460px] text-ds-18 leading-relaxed text-white/80">
                    {t("projectManagement:why.body")}
                  </p>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <DSButton to={TRIAL} variant="solid-light">{t("projectManagement:why.cta.trial")}</DSButton>
                    <DSButton to="/pricing" variant="ghost">{t("projectManagement:why.cta.pricing")}</DSButton>
                  </div>
                </div>
                <Reveal className="w-full lg:max-w-[440px] lg:justify-self-end">
                  <JobCostPanel />
                </Reveal>
              </div>
            </Container>
          </div>
        </div>
      </section>
    </MotionProvider>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   3. WHAT'S INSIDE — benefits bento (white-panel charts, as on the home bento)
   ───────────────────────────────────────────────────────────────────────── */

const SUBS = ["Daelmans BV", "VanderElst", "Pro-Klima", "Stevens & Co"];
const SCOPE_PILL_KEYS = [
  "inside.scope.pill.tiling",
  "inside.scope.pill.saturday",
  "inside.scope.pill.radiators",
  "inside.scope.pill.photo",
];

function WhatsInside() {
  const { t } = useLocale();
  const cardBody = "mt-2 text-ds-14 leading-relaxed text-ds-ink-soft";
  return (
    <MotionProvider>
      <section className="bg-bg font-ds leading-[1.24]">
        <Container className="py-14 md:py-20">
          <SectionHeading eyebrow={t("projectManagement:inside.eyebrow")} title={t("projectManagement:inside.title")} />

          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 lg:auto-rows-[minmax(232px,auto)]">
            {/* 7 cards over 3×3: two of them span two columns, so every cell is
                filled. The old 2-column-plus-stretch skeleton only balanced with
                5 cards — at 7 the right column stretched and left a dead gap. */}

            {/* 1 · Project planning & organisation */}
            <Reveal>
              <BentoCard className="bg-ds-accent">
                <h3 className="text-ds-22 font-semibold tracking-tight text-white">{t("projectManagement:inside.planning.title")}</h3>
                <p className="mt-2 text-ds-14 leading-relaxed text-white/85">{t("projectManagement:inside.planning.body")}</p>
                <div className="mt-auto pt-4">
                  {/* No backdrop-blur — 90% white; see the panel above (DIT-553). */}
                  <div className="rounded-2xl border border-white/60 bg-white/90 p-3.5 shadow-[0_18px_40px_-18px_rgba(15,23,42,0.4)]">
                    <div className="flex items-baseline justify-between text-ds-14">
                      <span className="text-ds-ink-soft">{t("projectManagement:panel.hoursLabel")}</span>
                      <span className="font-semibold text-ds-ink">184 / 240 h</span>
                    </div>
                    <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-bg-subtle">
                      <div className="h-full w-[77%] rounded-full bg-ds-accent" />
                    </div>
                    <p className="mt-1.5 text-[12px] text-ds-ink-soft">{t("projectManagement:inside.budget.daysLeft")}</p>
                  </div>
                </div>
              </BentoCard>
            </Reveal>

            {/* 2 · Project documents & photos */}
            <Reveal delay={0.06}>
              <BentoCard title={t("projectManagement:inside.docs.title")}>
                <p className={cardBody}>{t("projectManagement:inside.docs.body")}</p>
                <div className="mt-auto flex flex-col gap-2 pt-4">
                  {DOC_ROWS.map((r) => (
                    <div key={r.key} className="flex items-center gap-3 rounded-xl border border-line bg-white px-3 py-2">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-bg-subtle text-ds-accent"><r.icon size={14} strokeWidth={2} /></span>
                      <span className="flex-1 text-[13px] font-medium text-ds-ink">{t(`projectManagement:${r.key}`)}</span>
                      <span className="text-[12px] text-ds-ink-soft">{r.meta}</span>
                    </div>
                  ))}
                </div>
              </BentoCard>
            </Reveal>

            {/* 3 · Vehicle Track & Trace */}
            <Reveal delay={0.1}>
              <BentoCard>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-ds-22 font-semibold tracking-tight text-ds-ink">{t("projectManagement:inside.track.title")}</h3>
                  <span className="shrink-0 rounded-full border border-line bg-bg-subtle px-2.5 py-1 text-[11px] font-semibold text-ds-ink-soft">{t("projectManagement:inside.track.tag")}</span>
                </div>
                <p className={cardBody}>{t("projectManagement:inside.track.body")}</p>
                <div className="mt-auto flex flex-col gap-2 pt-4">
                  {TRACK_ROWS.map((r) => (
                    <div key={r.key} className="flex items-center gap-3 rounded-xl border border-line bg-white px-3 py-2">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-bg-subtle text-ds-accent"><r.icon size={14} strokeWidth={2} /></span>
                      <span className="flex-1 text-[13px] font-medium text-ds-ink">{t(`projectManagement:${r.key}`)}</span>
                    </div>
                  ))}
                </div>
              </BentoCard>
            </Reveal>

            {/* 4 · Subcontractor management — spans two */}
            <Reveal delay={0.14} className="lg:col-span-2">
              <BentoCard className="lg:flex-row lg:items-center lg:gap-8">
                <div className="lg:max-w-[280px]">
                  <h3 className="text-ds-22 font-semibold tracking-tight text-ds-ink">{t("projectManagement:inside.subs.title")}</h3>
                  <p className={cardBody}>{t("projectManagement:inside.subs.body")}</p>
                </div>
                <div className="mt-5 flex flex-1 flex-col gap-2 lg:mt-0">
                  {SUBS.map((s, i) => (
                    <div key={s} className="flex items-center gap-3 rounded-xl border border-line bg-white px-3 py-2">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ds-accent text-[12px] font-bold text-white">{s[0]}</span>
                      <span className="flex-1 text-[13px] font-medium text-ds-ink">{s}</span>
                      <span className="text-[12px] text-ds-ink-soft">{[18, 32, 12, 9][i]}h</span>
                    </div>
                  ))}
                </div>
              </BentoCard>
            </Reveal>

            {/* 5 · Project reports & history */}
            <Reveal delay={0.18}>
              <BentoCard title={t("projectManagement:inside.reports.title")}>
                <p className={cardBody}>{t("projectManagement:inside.reports.body")}</p>
                <div className="mt-auto flex flex-col gap-2 pt-4">
                  {REPORT_ROWS.map((r) => (
                    <div key={r.key} className="flex items-center gap-3 rounded-xl border border-line bg-white px-3 py-2">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-bg-subtle text-ds-accent"><r.icon size={14} strokeWidth={2} /></span>
                      <span className="flex-1 text-[13px] font-medium text-ds-ink">{t(`projectManagement:${r.key}`)}</span>
                    </div>
                  ))}
                </div>
              </BentoCard>
            </Reveal>

            {/* 6 · Mileage & travel — spans two, so the chart gets width instead of dead height */}
            <Reveal delay={0.22} className="lg:col-span-2">
              <BentoCard className="lg:flex-row lg:items-center lg:gap-8">
                <div className="lg:max-w-[280px]">
                  <h3 className="text-ds-22 font-semibold tracking-tight text-ds-ink">{t("projectManagement:inside.transport.title")}</h3>
                  <p className={cardBody}>{t("projectManagement:inside.transport.body")}</p>
                </div>
                <div className="mt-5 flex-1 lg:mt-0">
                  <div className="rounded-xl border border-line bg-white p-4">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-[13px] text-ds-ink-soft">
                        <Route size={16} strokeWidth={2} className="text-ds-accent" /> {t("projectManagement:inside.transport.travelLabel")}
                      </span>
                      <span className="text-[13px] font-semibold text-ds-ink">18 trips</span>
                    </div>
                    <Sparkline className="mt-3" data={[16, 22, 19, 30, 26, 38, 33, 50, 44]} highlight={7} />
                    <div className="mt-1.5 flex justify-between text-[11px] font-medium text-ds-ink-soft/70">
                      {["W1", "W2", "W3", "W4", "W5"].map((d) => <span key={d}>{d}</span>)}
                    </div>
                  </div>
                </div>
              </BentoCard>
            </Reveal>

            {/* 7 · Extra work & materials */}
            <Reveal delay={0.26}>
              <BentoCard title={t("projectManagement:inside.extra.title")}>
                <p className={cardBody}>{t("projectManagement:inside.extra.body")}</p>
                <div className="mt-auto pt-4">
                  <Marquee className="-mx-7">
                    {SCOPE_PILL_KEYS.map((k) => (
                      <span key={k} className="whitespace-nowrap rounded-full border border-line bg-white px-3 py-1.5 text-ds-14 font-medium text-ds-ink-soft shadow-sm">
                        <Receipt size={12} strokeWidth={2} className="mr-1.5 inline text-ds-accent" />{t(`projectManagement:${k}`)}
                      </span>
                    ))}
                  </Marquee>
                </div>
              </BentoCard>
            </Reveal>
          </div>
        </Container>
      </section>
    </MotionProvider>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   4. KEEP AN EYE ON YOUR CREW — monitoring stats bento (home bento style)
   ───────────────────────────────────────────────────────────────────────── */

function CrewSection() {
  const { market, t } = useLocale();
  return (
    <section className="bg-bg font-ds">
      <div className="px-4 py-3">
        <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-ds-hero">
          <img src={cmsImage(t, "projectManagement:crew.image", HERO_BG)} alt={t("projectManagement:crew.imageAlt")} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
          <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(8,11,22,0.45) 0%, rgba(8,11,22,0.35) 45%, rgba(8,11,22,0.8) 100%), linear-gradient(270deg, rgba(8,11,22,0) 40%, rgba(8,11,22,0.7) 100%)" }} />
          <div aria-hidden className="absolute inset-0" style={GRID_W} />
          {/* Container, not raw px-*: every other panel on the page aligns its text
              to the site content column, which only Container's max-w-content gives.
              A hand-rolled px-12 looks identical until the viewport passes the max
              width — then this text stays pinned to the panel edge while the rest
              of the page steps inward. */}
          <Container className="relative py-16 md:py-24">
            <div className="flex max-w-[680px] items-stretch gap-5">
              <span aria-hidden className="w-1 shrink-0 rounded-full bg-ds-gold" />
              <div>
                <SectionHeading
                  tone="dark"
                  eyebrow={t("projectManagement:crew.eyebrow")}
                  title={t("projectManagement:crew.title")}
                  intro={<>
                    {t("projectManagement:crew.intro")}
                    {market === "belgium" && " " + t("projectManagement:crew.intro.beSuffix")}
                  </>}
                />
              </div>
            </div>
          </Container>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   5. FROM OPEN TO INVOICE — connected steps (home pattern)
   ───────────────────────────────────────────────────────────────────────── */

const OPEN_STEP_KEYS = ["open", "logs", "margin"] as const;

function OpenToInvoice() {
  const { t } = useLocale();
  const steps: ConnectedStep[] = OPEN_STEP_KEYS.map((k) => ({
    title: t(`projectManagement:steps.${k}.title`),
    body: t(`projectManagement:steps.${k}.body`),
    image: cmsImage(t, `projectManagement:steps.${k}.image`, HERO_BG),
    alt: t(`projectManagement:steps.${k}.alt`),
  }));
  return (
    <ConnectedSteps
      eyebrow={<Eyebrow>{t("projectManagement:steps.eyebrow")}</Eyebrow>}
      title={<>{t("projectManagement:steps.title.pre")} <span className="text-ds-accent">{t("projectManagement:steps.title.highlight")}</span></>}
      description={t("projectManagement:steps.description")}
      actions={<DSButton to={TRIAL} variant="solid">{t("projectManagement:steps.cta")}</DSButton>}
      steps={steps}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   6. PROJECT PRICING — real plans
   ───────────────────────────────────────────────────────────────────────── */

function ProjectPricing() {
  const { t } = useLocale();
  const [annual, setAnnual] = useState(false);
  const isDesktop = useIsDesktop();
  return (
    <section className="bg-bg-subtle font-ds">
      <Container className="py-16 md:py-20">
        <div className="mx-auto max-w-2xl">
          <SectionHeading
            align="center"
            eyebrow={t("projectManagement:pricing.eyebrow")}
            title={t("projectManagement:pricing.title")}
            intro={t("projectManagement:pricing.intro")}
          />
        </div>

        <PricingToggle id="project" annual={annual} setAnnual={setAnnual} className="mb-14 mt-10 justify-center" />

        <div className="flex flex-col gap-6 lg:mx-auto lg:grid lg:max-w-[760px] lg:grid-cols-2 lg:gap-6">
          {PROJECT_PLANS.map((plan, i) => (
            <PricingPlanCard key={plan.name} plan={plan} annual={annual} index={i} isDesktop={isDesktop} fan={false} />
          ))}
        </div>

        <p className="mx-auto mt-8 max-w-[760px] text-center text-[13px] text-ink-faint lg:text-left">
          {t("projectManagement:pricing.breakdown.pre")}{" "}
          <LocaleLink to="/pricing" className="font-medium text-ds-accent hover:underline">{t("projectManagement:pricing.breakdown.link")}</LocaleLink>{t("projectManagement:pricing.breakdown.post")}
        </p>
      </Container>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   7. QUOTE — gold gradient panel with grid, white type
   ───────────────────────────────────────────────────────────────────────── */

function QuoteBand() {
  const { t } = useLocale();
  return (
    <section className="bg-bg font-ds">
      <div className="px-4 py-3">
        <div
          className="relative overflow-hidden rounded-[32px] px-6 py-16 text-center md:px-12 md:py-24"
          style={{ background: "linear-gradient(to bottom, var(--ds-warn), var(--ds-gold))" }}
        >
          <div className="pointer-events-none absolute inset-0" style={GRID_W} />
          <div className="relative">
            <Quote size={40} strokeWidth={1.6} className="mx-auto text-white/85" fill="currentColor" />
            <blockquote className="mx-auto mt-6 max-w-3xl text-ds-30 font-semibold leading-[1.2] tracking-[-0.5px] text-white lg:text-ds-38">
              {t("projectManagement:quote.text")}
            </blockquote>
            <figcaption className="mt-7 text-ds-14 font-semibold uppercase tracking-[0.1em] text-white/85">
              {t("projectManagement:quote.attribution")}
            </figcaption>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   8. CTA — decorative icon-tile grid framing the headline (home IndustryCta)
   ───────────────────────────────────────────────────────────────────────── */

const Y = "var(--ds-gold-ink)";
const B = "var(--ds-accent-deep)";
/* IconGridCta lays exactly 13 icons into fixed cells (ICON_AT, indices 0–12) —
   a 14th would silently not render on desktop. So this list stays at 13.
   Dropped: `costControl` / `margin` (the page was rebuilt away from finance —
   "Done-it doesn't handle financial or cost-related matters", client) and
   `navigation` / `time` / `overview` (those belong to Time, not Project).
   Added: the five operational keys shipped with the rebuild that had never
   been wired in here. */
const FEAT_DEFS: { key: string; Icon: CtaGridIcon["Icon"]; color: string }[] = [
  { key: "planning", Icon: CalendarClock, color: B },
  { key: "projects", Icon: FolderKanban, color: Y },
  { key: "subcontractors", Icon: Users, color: B },
  { key: "materials", Icon: Package, color: Y },
  { key: "extraWork", Icon: Receipt, color: B },
  { key: "documents", Icon: FileText, color: Y },
  { key: "photos", Icon: ImageIcon, color: B },
  { key: "transport", Icon: Route, color: Y },
  { key: "track", Icon: Car, color: B },
  { key: "reports", Icon: BarChart3, color: Y },
  { key: "crew", Icon: HardHat, color: B },
  { key: "geofencing", Icon: MapPin, color: Y },
  { key: "compliance", Icon: ShieldCheck, color: B },
];

function ProjectCta() {
  const { t } = useLocale();
  const items: CtaGridIcon[] = FEAT_DEFS.map((f) => ({
    name: t(`projectManagement:cta.feat.${f.key}`),
    Icon: f.Icon,
    color: f.color,
  }));
  return (
    <section className="bg-bg font-ds">
      <IconGridCta
        items={items}
        eyebrow={<Eyebrow>{t("projectManagement:cta.eyebrow")}</Eyebrow>}
        title={t("projectManagement:cta.title")}
        body={t("projectManagement:cta.body")}
        actions={
          <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
            <DSButton to={TRIAL} variant="solid">{t("projectManagement:cta.trial")}</DSButton>
            <LocaleLink to="/features" className="inline-flex items-center gap-1 text-ds-14 font-semibold text-ds-accent hover:underline">
              {t("projectManagement:cta.lookingForTime")} <ArrowRight size={15} strokeWidth={2} />
            </LocaleLink>
          </div>
        }
      />
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */

export default function ProjectManagement() {
  return (
    <>
      <CostControlHero />
      <WhyProject />
      <WhatsInside />
      <CrewSection />
      <FeatureOverview detailHref="/features" />
      <OpenToInvoice />
      <ProjectPricing />
      <QuoteBand />
      <ProjectCta />
    </>
  );
}
